import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { FornecedorService } from 'app/services/fornecedor.service';
import { FabricanteService } from 'app/services/fabricante.service';
import { DespachanteService } from 'app/services/despachante.service';
import { RastreadorModeloService } from 'app/services/rastreador-modelo.service';
import Swal from 'sweetalert2';
import { DatePipe } from '@angular/common';
import { RastreadorService } from 'app/services/rastreador.service';

@Component({
  selector: 'app-rastreador-entrada-form',
  templateUrl: './rastreador-entrada-form.component.html',
  styleUrls: ['./rastreador-entrada-form.component.scss'],
  providers: [DatePipe]
})
export class RastreadorEntradaFormComponent implements OnInit {
  public fornecedores: any[] = [];
  public fabricantes: any[] = [];
  public despachantes: any[] = [];
  public modelosRastreador: any[] = [];
  public selectedFornecedor: string | null = null;
  public selectedFabricante: string | null = null;
  public selectedDespachante: string | null = null;
  public selectedModeloRastreador: string | null = null;
  public dataCompra: string | null = null;
  public dataChegadaRemessa: string | null = null;
  public isLoading = false;
  public contentHeader: object;
  public today: string;
  public imeis: string | null = null;
  public loteId: number | null = null;
  public isViewMode = false;

  constructor(
    private fornecedorService: FornecedorService,
    private fabricanteService: FabricanteService,
    private despachanteService: DespachanteService,
    private rastreadorModeloService: RastreadorModeloService,
    private _router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private rastreadorService: RastreadorService
  ) { 
    this.today = new Date().toISOString().split('T')[0];
  }

  async ngOnInit() {
    this.contentHeader = {
      headerTitle: "Lote de Entrada de Rastreadores",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          { name: "Início", isLink: false },
          { name: "Rastreador Entrada", isLink: false },
          { name: this.route.snapshot.params['id'] ? "Visualizar" : "Cadastrar", isLink: false }
        ]
      }
    };

    this.loteId = this.route.snapshot.params['id'];
    this.isViewMode = !!this.loteId;

    try {
      this.fornecedores = await this.fornecedorService.get();
      this.fabricantes = await this.fabricanteService.get();
      this.despachantes = await this.despachanteService.get();
      this.modelosRastreador = await this.rastreadorModeloService.findAll();

      if (this.isViewMode) {
        const loteData = await this.rastreadorService.getOneEntrada(this.loteId);
        this.populateForm(loteData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      Swal.fire({
        title: "Erro!",
        text: "Não foi possível carregar os dados necessários. Confira se todos os itens estão cadastrados.",
        icon: "error",
        confirmButtonText: "Ok"
      });
    }
  }

  private populateForm(data: any[]) {
    // Get the first object from the array
    const firstItem = data[0];
    
    // Set the IMEIs from all objects
    const imeis = data.map(item => item.imei);
    this.imeis = imeis.join('\n');

    // Set the fields from the first object
    this.dataCompra = firstItem.dt_compra;
    this.dataChegadaRemessa = firstItem.dt_chegada;
    this.selectedModeloRastreador = firstItem.modelo_rastreador;
    this.selectedDespachante = firstItem.nome_despachante;
    this.selectedFabricante = firstItem.nome_fabricante;
    this.selectedFornecedor = firstItem.nome_fornecedor;
  }

  private isValidDate(dateStr: string): boolean {
    // Verifica o formato básico
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(dateStr)) return false;

    const [day, month, year] = dateStr.split('/').map(Number);
    
    // Verifica se os valores são números válidos
    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    
    // Verifica os limites dos meses
    if (month < 1 || month > 12) return false;
    
    // Verifica os dias de acordo com o mês
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) return false;
    
    return true;
  }

  private toISODate(dateStr: string): string | null {
    if (!this.isValidDate(dateStr)) return null;
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  public async submitForm(form: NgForm) {
    if (form.form.valid) {
      // Valida as datas
      if (!this.dataCompra || !this.dataChegadaRemessa) {
        Swal.fire({
          title: "Erro!",
          text: "Por favor, preencha ambas as datas.",
          icon: "error",
          confirmButtonText: "Ok"
        });
        return;
      }

      // Processa os IMEIs
      const imeiList = this.imeis?.split('\n').map(imei => imei.trim()).filter(imei => imei.length > 0);
      if (!imeiList || imeiList.length === 0) {
        Swal.fire({
          title: "Erro!",
          text: "Por favor, insira pelo menos um IMEI válido.",
          icon: "error",
          confirmButtonText: "Ok"
        });
        return;
      }

      // Confirmação com número de IMEIs
      const confirmResult = await Swal.fire({
        title: 'Confirmação',
        html: `Você está prestes a salvar um lote de entrada com <b>${imeiList.length}</b> IMEI(s).<br>Deseja continuar?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, salvar!',
        cancelButtonText: 'Cancelar'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      this.isLoading = true;
      try {
        const payload = {
          fornecedorId: this.selectedFornecedor,
          fabricanteId: this.selectedFabricante,
          despachanteId: this.selectedDespachante,
          modeloRastreadorId: this.selectedModeloRastreador,
          dataCompra: this.dataCompra,
          dataChegadaRemessa: this.dataChegadaRemessa,
          imeis: this.imeis?.split('\n').map(imei => imei.trim()).filter(imei => imei.length > 0)
        };

        // Chamada ao service para enviar os dados
        const res = await this.rastreadorService.entradaRastreador(payload);

        if (res.statusCode == 200) {
          await Swal.fire({
            title: "Erro!",
            text: res.message[0],
            icon: "error",
            confirmButtonText: "Ok"
          });
        } else {
          await Swal.fire({
            title: "Sucesso!",
            text: `Dados salvos com sucesso. ${imeiList.length} IMEI(s) processados.`,
            icon: "success",
            confirmButtonText: "Confirmar"
          });
          this._router.navigate(['rastreador']);
        }
        
      } catch (error) {
        console.error('Erro na operação:', error);
        await Swal.fire({
          title: "Erro!",
          text: "Ocorreu um erro ao processar a requisição.",
          icon: "error",
          confirmButtonText: "Ok"
        });
      } finally {
        this.isLoading = false;
      }
    } else {
      form.form.markAllAsTouched();
    }
  }

  public async importCSV(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target?.result as string;
        // Processa o conteúdo do CSV
        const imeis = content
          .split(/[,\n;]/) // Divide por vírgula, ponto e vírgula ou quebra de linha
          .map(imei => imei.replace(/\D/g, '')) // Remove tudo que não for número
          .filter(imei => imei.length > 0) // Remove valores vazios
          .join('\n'); // Junta com quebras de linha
        
        this.imeis = imeis;
      };

      reader.readAsText(file);
    }
  }

  public onBack(): void {
    this._router.navigate(['/rastreador']);
  }
}
